import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flame, TrendingUp, ArrowRight } from "lucide-react";

export default function ShareView() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/share/card/${shareId}`)
      .then((r) => setCard(r.data))
      .catch(() => setError(true));
  }, [shareId]);

  const isWinStreak = card?.streak_type === "win" && card?.current_streak > 0;

  return (
    <div className="min-h-screen bg-[#030712] relative flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-transparent to-teal-900/10" />

      <div className="relative z-10 w-full max-w-lg">
        {error ? (
          <Card className="glass-effect border border-white/10">
            <CardContent className="p-10 text-center" data-testid="share-not-found">
              <p className="text-gray-400 mb-4">This share card doesn't exist or has expired.</p>
              <Button onClick={() => navigate("/")} className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-bold">
                Explore TradeInsights
              </Button>
            </CardContent>
          </Card>
        ) : !card ? (
          <p className="text-center text-gray-500">Loading…</p>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="glass-effect border border-[#00E0FF]/20 overflow-hidden" data-testid="share-card-public">
              <CardContent className="p-8">
                <div className="text-xs font-bold text-[#00E0FF] uppercase tracking-[0.2em] mb-1">
                  TradeInsights AI
                </div>
                <div className="text-sm text-gray-400 mb-6">{card.trading_style}</div>

                <div className="text-center mb-8">
                  {isWinStreak ? (
                    <>
                      <div className="flex items-center justify-center gap-3">
                        <Flame className="w-10 h-10 text-[#00FFA3]" />
                        <span className="text-6xl font-bold text-[#00FFA3] mono" data-testid="share-streak-value">
                          {card.current_streak}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-white mt-2" style={{ fontFamily: "Unbounded" }}>
                        WIN STREAK
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Best run: {card.max_win_streak} wins in a row</div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center gap-3">
                        <TrendingUp className="w-10 h-10 text-[#00E0FF]" />
                        <span className="text-6xl font-bold text-white mono">{card.win_rate}%</span>
                      </div>
                      <div className="text-lg font-bold text-white mt-2" style={{ fontFamily: "Unbounded" }}>
                        WIN RATE
                      </div>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 text-center mb-6">
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <div className={`text-xl font-bold mono ${card.win_rate >= 50 ? "text-[#00FFA3]" : "text-[#FF3B30]"}`}>
                      {card.win_rate}%
                    </div>
                    <div className="text-[10px] text-gray-500 uppercase">Win rate</div>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <div className={`text-xl font-bold mono ${card.total_pnl >= 0 ? "text-[#00FFA3]" : "text-[#FF3B30]"}`}>
                      ${card.total_pnl}
                    </div>
                    <div className="text-[10px] text-gray-500 uppercase">Total P&L</div>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <div className="text-xl font-bold mono text-[#F59E0B]">{card.grade}</div>
                    <div className="text-[10px] text-gray-500 uppercase">AI grade</div>
                  </div>
                </div>

                <div className="text-center text-xs text-gray-500 mb-6">
                  {card.wins}W / {card.losses}L · {card.closed_trades} closed trades
                </div>

                <Button
                  data-testid="share-cta-btn"
                  onClick={() => navigate(card.referral_code ? `/?ref=${card.referral_code}` : "/")}
                  className="w-full bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-bold"
                >
                  Get your own AI trading signals
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-[10px] text-gray-600 text-center mt-3">
                  AI-powered predictions · not financial advice
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
